import { defineMessages } from '@edx/frontend-platform/i18n';

const messages = defineMessages({
  commonQuickLinksTitle: {
    id: 'catalog.static.common.quick-links-title',
    defaultMessage: 'Quick links',
    description: 'Title for the quick links card on static catalog pages.',
  },
  commonQuickLinksCourses: {
    id: 'catalog.static.common.quick-links-courses',
    defaultMessage: 'Explore courses',
    description: 'Quick link label to the course catalog.',
  },
  commonQuickLinksFaq: {
    id: 'catalog.static.common.quick-links-faq',
    defaultMessage: 'Read the FAQ',
    description: 'Quick link label to the FAQ page.',
  },
  commonQuickLinksHelp: {
    id: 'catalog.static.common.quick-links-help',
    defaultMessage: 'Contact support',
    description: 'Quick link label to the help page.',
  },
  commonContactTitle: {
    id: 'catalog.static.common.contact-title',
    defaultMessage: 'Need a direct conversation?',
    description: 'Title for the contact card on static catalog pages.',
  },
  commonContactDescription: {
    id: 'catalog.static.common.contact-description',
    defaultMessage: 'Our team can answer questions about enrollment, platform usage, or managing your account.',
    description: 'Description for the contact card on static catalog pages.',
  },
  commonContactButton: {
    id: 'catalog.static.common.contact-button',
    defaultMessage: 'Write to the team',
    description: 'Button label for the contact card on static catalog pages.',
  },
  commonUpdatedLabel: {
    id: 'catalog.static.common.updated-label',
    defaultMessage: 'Reference',
    description: 'Label shown next to the document reference badge on static pages.',
  },
  commonUpdatedValue: {
    id: 'catalog.static.common.updated-value',
    defaultMessage: 'General information version',
    description: 'Reference badge value shown on static pages.',
  },
  aboutMeta: {
    id: 'catalog.static.about.meta',
    defaultMessage: 'About',
    description: 'Document metadata label for the about page.',
  },
  aboutTitle: {
    id: 'catalog.static.about.title',
    defaultMessage: 'A platform designed for clear learning',
    description: 'Title for the about page.',
  },
  aboutLead: {
    id: 'catalog.static.about.lead',
    defaultMessage: 'WutiSkill designs a structured, professional, and demanding learning experience with a simple goal: help everyone progress through useful, readable, directly applicable courses.',
    description: 'Lead paragraph for the about page.',
  },
  aboutSectionMissionTitle: {
    id: 'catalog.static.about.section.mission.title',
    defaultMessage: 'Our mission',
    description: 'Mission section title on the about page.',
  },
  aboutSectionMissionBody: {
    id: 'catalog.static.about.section.mission.body',
    defaultMessage: 'We build a platform centered on real progression. Every path should help learners develop concrete skills, understand what they are doing, and move forward without unnecessary friction.',
    description: 'Mission section body on the about page.',
  },
  aboutSectionApproachTitle: {
    id: 'catalog.static.about.section.approach.title',
    defaultMessage: 'Our approach',
    description: 'Approach section title on the about page.',
  },
  aboutSectionApproachBody: {
    id: 'catalog.static.about.section.approach.body',
    defaultMessage: 'We favor clear content, progressive teaching structure, and a sober interface. The goal is not to add noise, but to make learning more efficient, readable, and lasting.',
    description: 'Approach section body on the about page.',
  },
  aboutSectionAudienceTitle: {
    id: 'catalog.static.about.section.audience.title',
    defaultMessage: 'Who is WutiSkill for?',
    description: 'Audience section title on the about page.',
  },
  aboutSectionAudienceBody: {
    id: 'catalog.static.about.section.audience.body',
    defaultMessage: 'The platform is for learners, professionals, teams, and organizations looking for serious, outcome-oriented training designed for measurable skill growth.',
    description: 'Audience section body on the about page.',
  },
  faqMeta: {
    id: 'catalog.static.faq.meta',
    defaultMessage: 'FAQ',
    description: 'Document metadata label for the FAQ page.',
  },
  faqTitle: {
    id: 'catalog.static.faq.title',
    defaultMessage: 'Frequently asked questions',
    description: 'Title for the FAQ page.',
  },
  faqLead: {
    id: 'catalog.static.faq.lead',
    defaultMessage: 'This page gathers general answers to common questions about accounts, enrollment, personal data, and platform use.',
    description: 'Lead paragraph for the FAQ page.',
  },
  faqQuestionAccountTitle: {
    id: 'catalog.static.faq.question.account.title',
    defaultMessage: 'How do I create an account?',
    description: 'FAQ question about account creation.',
  },
  faqQuestionAccountAnswer: {
    id: 'catalog.static.faq.question.account.answer',
    defaultMessage: 'You can create an account directly from the public platform. Once registered, you can access open courses, enroll in available sessions, and track your progress from your personal space.',
    description: 'FAQ answer about account creation.',
  },
  faqQuestionEmailConfirmationTitle: {
    id: 'catalog.static.faq.question.email-confirmation.title',
    defaultMessage: 'What should I do if I do not receive the confirmation email?',
    description: 'FAQ question about missing confirmation email.',
  },
  faqQuestionEmailConfirmationAnswer: {
    id: 'catalog.static.faq.question.email-confirmation.answer',
    defaultMessage: 'Check your spam or promotions folder first, then confirm that the email address was entered correctly. If the message still does not arrive after a few minutes, contact support with the email address used during registration.',
    description: 'FAQ answer about missing confirmation email.',
  },
  faqQuestionPasswordResetTitle: {
    id: 'catalog.static.faq.question.password-reset.title',
    defaultMessage: 'How do I recover my password?',
    description: 'FAQ question about password recovery.',
  },
  faqQuestionPasswordResetAnswer: {
    id: 'catalog.static.faq.question.password-reset.answer',
    defaultMessage: 'Use the password reset link on the sign-in page. You will receive an email with instructions to create a new password. Choose a password you do not use on shared computers or other services.',
    description: 'FAQ answer about password recovery.',
  },
  faqQuestionAndroidTitle: {
    id: 'catalog.static.faq.question.android.title',
    defaultMessage: 'Can I take courses from an Android phone?',
    description: 'FAQ question about using the platform on Android.',
  },
  faqQuestionAndroidAnswer: {
    id: 'catalog.static.faq.question.android.answer',
    defaultMessage: 'Yes. The platform works in a mobile browser on Android phones. For the best experience, use an up-to-date version of Chrome, keep your phone charged, and switch to Wi-Fi when watching longer videos.',
    description: 'FAQ answer about using the platform on Android.',
  },
  faqQuestionLowBandwidthTitle: {
    id: 'catalog.static.faq.question.low-bandwidth.title',
    defaultMessage: 'Does the platform work with a weak Internet connection?',
    description: 'FAQ question about weak Internet connections.',
  },
  faqQuestionLowBandwidthAnswer: {
    id: 'catalog.static.faq.question.low-bandwidth.answer',
    defaultMessage: 'Most text pages, quizzes, and discussions can work on modest connections, but videos and live sessions need more stable bandwidth. If your connection is slow, close other apps, lower video quality when available, and retry during off-peak hours.',
    description: 'FAQ answer about weak Internet connections.',
  },
  faqQuestionOfflineTitle: {
    id: 'catalog.static.faq.question.offline.title',
    defaultMessage: 'Can I download videos or materials to learn offline?',
    description: 'FAQ question about offline access.',
  },
  faqQuestionOfflineAnswer: {
    id: 'catalog.static.faq.question.offline.answer',
    defaultMessage: 'Some courses may provide downloadable files such as PDFs or documents. Video download availability depends on the course settings and rights for that content. The course page or courseware will show when a file can be downloaded.',
    description: 'FAQ answer about offline access.',
  },
  faqQuestionDataUsageTitle: {
    id: 'catalog.static.faq.question.data-usage.title',
    defaultMessage: 'How much mobile data should I plan for a course?',
    description: 'FAQ question about mobile data usage.',
  },
  faqQuestionDataUsageAnswer: {
    id: 'catalog.static.faq.question.data-usage.answer',
    defaultMessage: 'Data use depends mostly on video length and quality. Reading lessons and answering quizzes usually consume little data, while video lessons can consume much more. When possible, use Wi-Fi for video, downloads, and live sessions.',
    description: 'FAQ answer about mobile data usage.',
  },
  faqQuestionPricingTitle: {
    id: 'catalog.static.faq.question.pricing.title',
    defaultMessage: 'Are courses free or paid?',
    description: 'FAQ question about free and paid courses.',
  },
  faqQuestionPricingAnswer: {
    id: 'catalog.static.faq.question.pricing.answer',
    defaultMessage: 'It depends on the course or program. Some learning paths may be open, while others may require payment, an invitation, or access through an organization. The course page displays the available enrollment option.',
    description: 'FAQ answer about free and paid courses.',
  },
  faqQuestionEnrollmentTitle: {
    id: 'catalog.static.faq.question.enrollment.title',
    defaultMessage: 'How do I enroll in a course?',
    description: 'FAQ question about course enrollment.',
  },
  faqQuestionEnrollmentAnswer: {
    id: 'catalog.static.faq.question.enrollment.answer',
    defaultMessage: 'Open the course page, review the dates and requirements, then choose the enrollment option shown on the page. If the course is private or reserved for an organization, you may need an invitation or approval before access is enabled.',
    description: 'FAQ answer about course enrollment.',
  },
  faqQuestionPacingTitle: {
    id: 'catalog.static.faq.question.pacing.title',
    defaultMessage: 'Can I take courses at my own pace?',
    description: 'FAQ question about pacing.',
  },
  faqQuestionPacingAnswer: {
    id: 'catalog.static.faq.question.pacing.answer',
    defaultMessage: 'It depends on the course. Some content is self-paced, while other courses follow a schedule set by the course team. The course page always specifies the delivery mode and useful dates.',
    description: 'FAQ answer about pacing.',
  },
  faqQuestionCertificatesTitle: {
    id: 'catalog.static.faq.question.certificates.title',
    defaultMessage: 'Are certificates available?',
    description: 'FAQ question about certificates.',
  },
  faqQuestionCertificatesAnswer: {
    id: 'catalog.static.faq.question.certificates.answer',
    defaultMessage: 'Depending on the path, a certificate may be offered at the end of the course. Completion requirements and access mode are shown in each training page.',
    description: 'FAQ answer about certificates.',
  },
  faqQuestionProgressTitle: {
    id: 'catalog.static.faq.question.progress.title',
    defaultMessage: 'How do I track my progress in a course?',
    description: 'FAQ question about course progress tracking.',
  },
  faqQuestionProgressAnswer: {
    id: 'catalog.static.faq.question.progress.answer',
    defaultMessage: 'Once enrolled, open the course from your learner dashboard. The course pages and progress area show completed content, grades when available, and the activities that still require attention.',
    description: 'FAQ answer about course progress tracking.',
  },
  faqQuestionVideoIssueTitle: {
    id: 'catalog.static.faq.question.video-issue.title',
    defaultMessage: 'What should I do if a video does not load?',
    description: 'FAQ question about video loading issues.',
  },
  faqQuestionVideoIssueAnswer: {
    id: 'catalog.static.faq.question.video-issue.answer',
    defaultMessage: 'Refresh the page, check your connection, and try another browser if possible. On mobile, switch between mobile data and Wi-Fi to compare. If the issue continues, send support the course name, video title, and a screenshot.',
    description: 'FAQ answer about video loading issues.',
  },
  faqQuestionQuizIssueTitle: {
    id: 'catalog.static.faq.question.quiz-issue.title',
    defaultMessage: 'What should I do if a quiz or assignment does not display correctly?',
    description: 'FAQ question about quiz or assignment display issues.',
  },
  faqQuestionQuizIssueAnswer: {
    id: 'catalog.static.faq.question.quiz-issue.answer',
    defaultMessage: 'Refresh the page and make sure your browser is up to date. If you are using a phone, try landscape mode or a computer for complex activities. If the problem persists, contact support with the course name, unit title, and screenshot.',
    description: 'FAQ answer about quiz or assignment display issues.',
  },
  faqQuestionDataTitle: {
    id: 'catalog.static.faq.question.data.title',
    defaultMessage: 'How is my data used?',
    description: 'FAQ question about personal data.',
  },
  faqQuestionDataAnswer: {
    id: 'catalog.static.faq.question.data.answer',
    defaultMessage: 'Information collected during registration and platform use is used strictly to develop better courses, improve the user experience, and measure overall engagement. User data is hosted in Europe.',
    description: 'FAQ answer about personal data.',
  },
  faqQuestionDeletionTitle: {
    id: 'catalog.static.faq.question.deletion.title',
    defaultMessage: 'What happens when I delete my account?',
    description: 'FAQ question about account deletion.',
  },
  faqQuestionDeletionAnswer: {
    id: 'catalog.static.faq.question.deletion.answer',
    defaultMessage: 'When you delete your account, associated data is completely removed from the platform within a maximum of 72 hours.',
    description: 'FAQ answer about account deletion.',
  },
  helpMeta: {
    id: 'catalog.static.help.meta',
    defaultMessage: 'Support',
    description: 'Document metadata label for the help page.',
  },
  helpTitle: {
    id: 'catalog.static.help.title',
    defaultMessage: 'Help center',
    description: 'Title for the help page.',
  },
  helpLead: {
    id: 'catalog.static.help.lead',
    defaultMessage: 'If you need help before or after enrollment, this page summarizes contact points and the most common cases handled by the support team.',
    description: 'Lead paragraph for the help page.',
  },
  helpSectionEnrollmentTitle: {
    id: 'catalog.static.help.section.enrollment.title',
    defaultMessage: 'Enrollment and access',
    description: 'Help section title about enrollment.',
  },
  helpSectionEnrollmentBody: {
    id: 'catalog.static.help.section.enrollment.body',
    defaultMessage: 'We can help if you cannot enroll in a session, if course access does not activate correctly, or if you need clarification about dates, pace, or access conditions.',
    description: 'Help section body about enrollment.',
  },
  helpSectionAccountTitle: {
    id: 'catalog.static.help.section.account.title',
    defaultMessage: 'Account and security',
    description: 'Help section title about account and security.',
  },
  helpSectionAccountBody: {
    id: 'catalog.static.help.section.account.body',
    defaultMessage: 'The team can assist with questions related to login, the email address associated with your account, account deletion, or access to your personal information.',
    description: 'Help section body about account and security.',
  },
  helpSectionLearningTitle: {
    id: 'catalog.static.help.section.learning.title',
    defaultMessage: 'Platform usage',
    description: 'Help section title about platform usage.',
  },
  helpSectionLearningBody: {
    id: 'catalog.static.help.section.learning.body',
    defaultMessage: 'If you have difficulty reading content, progressing through a course, or accessing a certificate, describe the issue precisely so the team can respond quickly.',
    description: 'Help section body about platform usage.',
  },
  helpAsideTitle: {
    id: 'catalog.static.help.aside.title',
    defaultMessage: 'Before writing to us',
    description: 'Aside title on the help page.',
  },
  helpAsideBodyOne: {
    id: 'catalog.static.help.aside.body-one',
    defaultMessage: 'Include the relevant course name, your account email address, and a concise description of the problem.',
    description: 'First aside paragraph on the help page.',
  },
  helpAsideBodyTwo: {
    id: 'catalog.static.help.aside.body-two',
    defaultMessage: 'If the request concerns personal data or account deletion, mention it at the beginning of your message to speed up processing.',
    description: 'Second aside paragraph on the help page.',
  },
  termsMeta: {
    id: 'catalog.static.terms.meta',
    defaultMessage: 'Terms',
    description: 'Document metadata label for the terms page.',
  },
  termsTitle: {
    id: 'catalog.static.terms.title',
    defaultMessage: 'Terms of service',
    description: 'Title for the terms page.',
  },
  termsLead: {
    id: 'catalog.static.terms.lead',
    defaultMessage: 'These terms describe the general rules that apply to platform use, content access, and user account management.',
    description: 'Lead paragraph for the terms page.',
  },
  termsSectionAcceptanceTitle: {
    id: 'catalog.static.terms.section.acceptance.title',
    defaultMessage: 'Acceptance of terms',
    description: 'Terms section title about acceptance.',
  },
  termsSectionAcceptanceBody: {
    id: 'catalog.static.terms.section.acceptance.body',
    defaultMessage: 'By using the platform, you agree to follow these terms and any specific rules indicated on certain courses, services, or features.',
    description: 'Terms section body about acceptance.',
  },
  termsSectionAccountTitle: {
    id: 'catalog.static.terms.section.account.title',
    defaultMessage: 'User account',
    description: 'Terms section title about accounts.',
  },
  termsSectionAccountBody: {
    id: 'catalog.static.terms.section.account.body',
    defaultMessage: 'You are responsible for the information provided during registration, the confidentiality of your credentials, and the use of your account. Each account is personal and must not be shared without authorization.',
    description: 'Terms section body about accounts.',
  },
  termsSectionUsageTitle: {
    id: 'catalog.static.terms.section.usage.title',
    defaultMessage: 'Use of content',
    description: 'Terms section title about content usage.',
  },
  termsSectionUsageBody: {
    id: 'catalog.static.terms.section.usage.body',
    defaultMessage: 'Content offered on the platform is intended for learning use consistent with its purpose. Any attempt to misuse, abuse access, or reproduce content without authorization may result in access restrictions.',
    description: 'Terms section body about content usage.',
  },
  termsSectionAvailabilityTitle: {
    id: 'catalog.static.terms.section.availability.title',
    defaultMessage: 'Service availability',
    description: 'Terms section title about service availability.',
  },
  termsSectionAvailabilityBody: {
    id: 'catalog.static.terms.section.availability.body',
    defaultMessage: 'The platform may evolve, be updated, or experience temporary interruptions. We aim to maintain a reliable service without guaranteeing absolute availability in all circumstances.',
    description: 'Terms section body about service availability.',
  },
  termsSectionChangesTitle: {
    id: 'catalog.static.terms.section.changes.title',
    defaultMessage: 'Changes and termination',
    description: 'Terms section title about changes and termination.',
  },
  termsSectionChangesBody: {
    id: 'catalog.static.terms.section.changes.body',
    defaultMessage: 'We may evolve the platform, adjust certain features, or modify these terms to reflect actual usage, technical constraints, or applicable legal obligations.',
    description: 'Terms section body about changes and termination.',
  },
  privacyMeta: {
    id: 'catalog.static.privacy.meta',
    defaultMessage: 'Privacy',
    description: 'Document metadata label for the privacy page.',
  },
  privacyTitle: {
    id: 'catalog.static.privacy.title',
    defaultMessage: 'Privacy policy',
    description: 'Title for the privacy page.',
  },
  privacyLead: {
    id: 'catalog.static.privacy.lead',
    defaultMessage: 'This policy describes in general terms what information is collected, why it is used, and how it is deleted when you request account closure.',
    description: 'Lead paragraph for the privacy page.',
  },
  privacySectionHostingTitle: {
    id: 'catalog.static.privacy.section.hosting.title',
    defaultMessage: 'Data hosting',
    description: 'Privacy section title about hosting.',
  },
  privacySectionHostingBody: {
    id: 'catalog.static.privacy.section.hosting.body',
    defaultMessage: 'User data is hosted in Europe. This includes account information, enrollment, learning progress, and technical data required to operate the service.',
    description: 'Privacy section body about hosting.',
  },
  privacySectionCollectionTitle: {
    id: 'catalog.static.privacy.section.collection.title',
    defaultMessage: 'Information collected',
    description: 'Privacy section title about collection.',
  },
  privacySectionCollectionBody: {
    id: 'catalog.static.privacy.section.collection.body',
    defaultMessage: 'We may collect information during registration, platform browsing, course enrollment, and normal use of learning features.',
    description: 'Privacy section body about collection.',
  },
  privacySectionUseTitle: {
    id: 'catalog.static.privacy.section.use.title',
    defaultMessage: 'Use of data',
    description: 'Privacy section title about data usage.',
  },
  privacySectionUseBody: {
    id: 'catalog.static.privacy.section.use.body',
    defaultMessage: 'Information collected during registration and platform use is used strictly to develop better courses, improve platform use, and make the learning experience more relevant.',
    description: 'Privacy section body about data usage.',
  },
  privacySectionAnalyticsTitle: {
    id: 'catalog.static.privacy.section.analytics.title',
    defaultMessage: 'Engagement measurement',
    description: 'Privacy section title about analytics.',
  },
  privacySectionAnalyticsBody: {
    id: 'catalog.static.privacy.section.analytics.body',
    defaultMessage: 'We use Google Analytics to measure overall engagement, understand path usage, and identify platform improvement points.',
    description: 'Privacy section body about analytics.',
  },
  privacySectionDeletionTitle: {
    id: 'catalog.static.privacy.section.deletion.title',
    defaultMessage: 'Data deletion',
    description: 'Privacy section title about account deletion.',
  },
  privacySectionDeletionBody: {
    id: 'catalog.static.privacy.section.deletion.body',
    defaultMessage: 'When you delete your account, associated data is completely removed from the platform within a maximum of 72 hours.',
    description: 'Privacy section body about account deletion.',
  },
  privacySectionRightsTitle: {
    id: 'catalog.static.privacy.section.rights.title',
    defaultMessage: 'Questions and requests',
    description: 'Privacy section title about privacy rights and requests.',
  },
  privacySectionRightsBody: {
    id: 'catalog.static.privacy.section.rights.body',
    defaultMessage: 'For any question about your data, its use, or account deletion, you can contact the team through the address shown on this page.',
    description: 'Privacy section body about privacy questions.',
  },
});

export default messages;
